// src/components/ReplayTool.jsx
// "Fire this captured request again, right now" tool — replays a request
// through the proxy's replay endpoint and lets the user diff the fresh
// response against what was originally captured.
//
// Contract (proxy-server, POST <PROXY_API_BASE>/proxy/replay):
//   body: { url, method?, headers?, body? }
//   -> { ok, status, headers, body, bodyTruncated, durationMs }
//      | { ok:false, error }
//
// Modal idiom copied from DiffTool.jsx's DiffToolModal (plain overlay, no
// MUI Dialog) — same CSS-var styling, same clipboard-copy fallback pattern.
//
// Diff engine consumed via the pinned contract in ../utils/jsonDiff:
//   jsonDiff(left, right, opts) -> { same, truncated?, counts, entries: [{path, kind, left, right}] }

import React from "react";
import { jsonDiff } from "../utils/jsonDiff";
import { buildCurl } from "../utils/curl";

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

/* ── clipboard helper — same approach as InspectraNetworkView's copyToClipboard,
   reimplemented locally (mirrors DiffTool.jsx's local copy). ── */
function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(String(text));
      return true;
    }
  } catch { /* fall through to legacy path */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = String(text);
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand("copy");
    ta.remove();
    return true;
  } catch { return false; }
}

/* ── formatting helpers ── */

function bodyToText(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function tryPrettyJson(text) {
  if (typeof text !== "string" || !text.trim()) return { ok: false };
  try { return { ok: true, value: JSON.parse(text) }; } catch { return { ok: false }; }
}

function prettyPrintMaybe(text) {
  const parsed = tryPrettyJson(text);
  if (!parsed.ok) return text;
  try { return JSON.stringify(parsed.value, null, 2); } catch { return text; }
}

function headersObjToText(obj) {
  return Object.entries(obj || {}).map(([k, v]) => `${k}: ${v}`).join("\n");
}

function headersTextToObj(text) {
  const out = {};
  (text || "").split("\n").forEach((line) => {
    const idx = line.indexOf(":");
    if (idx === -1) return;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (key) out[key] = value;
  });
  return out;
}

function statusStyle(status) {
  const n = Number(status) || 0;
  if (n >= 200 && n < 300) return { background: "var(--success-bg, transparent)", color: "var(--success-text)", borderColor: "var(--success-text)" };
  if (n >= 300 && n < 400) return { background: "var(--warn-bg, transparent)", color: "var(--warn-text)", borderColor: "var(--warn-text)" };
  if (n >= 400) return { background: "var(--error-bg, transparent)", color: "var(--error-text)", borderColor: "var(--error-text)" };
  return { color: "var(--muted)", borderColor: "var(--line)" };
}

/* ── cURL builder — from the CURRENT edited fields. shellSingleQuote /
   buildCurl / headersTextToObjOrdered now live in ../utils/curl (WP6,
   launch-signoff-spec.md §8) — imported above, extracted verbatim so
   buildFindingTicket.js can reuse them without pulling in this component. ── */

/* ── diff table (compact — mirrors DiffTool's table styling) ── */

function stringifyCompact(v) {
  if (v === undefined) return "";
  if (v === null) return "null";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}

const MAX_CELL_LEN = 120;
function Cell({ value }) {
  const text = stringifyCompact(value);
  if (text.length <= MAX_CELL_LEN) return <span>{text || "—"}</span>;
  return <span title={text}>{text.slice(0, MAX_CELL_LEN)}…</span>;
}

const KIND_STYLE = {
  added: { background: "var(--success-bg, transparent)", color: "var(--success-text)" },
  removed: { background: "var(--error-bg, transparent)", color: "var(--error-text)" },
  changed: { background: "var(--warn-bg, transparent)", color: "var(--warn-text)" },
  same: { color: "var(--ink)" },
};

function ReplayDiffResult({ result }) {
  if (!result) return null;
  const { same, truncated, counts, entries } = result;
  const total = (counts?.added || 0) + (counts?.removed || 0) + (counts?.changed || 0) + (counts?.same || 0);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={s.summary}>
        {same
          ? "identical"
          : `${counts?.changed || 0} changed · ${counts?.added || 0} added · ${counts?.removed || 0} removed (of ${total} fields)`}
        {truncated && <span style={s.truncNote}> — truncated, showing partial diff</span>}
      </div>
      {!same && (
        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Path</th>
                <th style={s.th}>Original</th>
                <th style={s.th}>Replay</th>
              </tr>
            </thead>
            <tbody>
              {(entries || []).map((e, i) => (
                <tr key={e.path || i} style={KIND_STYLE[e.kind] || {}}>
                  <td style={s.k} title={e.path || "(root)"}>{e.path || "(root)"}</td>
                  <td style={s.v}><Cell value={e.left} /></td>
                  <td style={s.v}><Cell value={e.right} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── main modal ── */

export function ReplayModal({ open, onClose, request }) {
  const [method, setMethod] = React.useState("GET");
  const [url, setUrl] = React.useState("");
  const [headersText, setHeadersText] = React.useState("");
  const [bodyText, setBodyText] = React.useState("");

  const [firing, setFiring] = React.useState(false);
  const [fireError, setFireError] = React.useState(null);
  const [result, setResult] = React.useState(null); // { ok, status, headers, body, bodyTruncated, durationMs }
  const [headersOpen, setHeadersOpen] = React.useState(false);
  const [copyFeedback, setCopyFeedback] = React.useState(null);

  const [diffResult, setDiffResult] = React.useState(null);
  const [diffNote, setDiffNote] = React.useState(null);
  const [diffOpen, setDiffOpen] = React.useState(false);

  // Prefill from the captured request when the modal opens or a DIFFERENT request is
  // picked. Keyed by the request's id, not the object: live traffic replaces row
  // objects every few seconds, and keying on the object wiped the tester's edits
  // and the fired response mid-replay (2026-10-18).
  const requestKey = request ? (request.requestId ?? request.id ?? request._k ?? request.url ?? null) : null;
  React.useEffect(() => {
    if (!open) return;
    try {
      setMethod((request?.method || "GET").toUpperCase());
      setUrl(String(request?.url || ""));
      setHeadersText(headersObjToText(request?.requestHeaders || {}));
      setBodyText(prettyPrintMaybe(bodyToText(request?.requestBody)));
    } catch {
      setMethod("GET");
      setUrl("");
      setHeadersText("");
      setBodyText("");
    }
    setFiring(false);
    setFireError(null);
    setResult(null);
    setDiffResult(null);
    setDiffNote(null);
    setDiffOpen(false);
    setCopyFeedback(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- request read at open/switch only (see above)
  }, [open, requestKey]);

  if (!open) return null;

  const doCopy = (text, label) => {
    const ok = copyToClipboard(text);
    setCopyFeedback(ok ? `${label} copied` : `${label} copy failed`);
    setTimeout(() => setCopyFeedback(null), 1500);
  };

  const handleFire = async () => {
    setFiring(true);
    setFireError(null);
    setResult(null);
    setDiffResult(null);
    setDiffNote(null);
    setDiffOpen(false);
    try {
      const payload = {
        url: url || "",
        ...(method ? { method } : {}),
        ...(Object.keys(headersTextToObj(headersText)).length ? { headers: headersTextToObj(headersText) } : {}),
        ...(bodyText && bodyText.trim() ? { body: bodyText } : {}),
      };
      const res = await fetch(`${PROXY_API_BASE}/proxy/replay`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      let json = null;
      try { json = await res.json(); } catch { /* non-JSON body */ }
      if (!res.ok || !json) {
        setFireError((json && json.error) || `Replay failed (${res.status})`);
      } else if (json.ok === false) {
        setFireError(json.error || "Replay failed");
      } else {
        setResult(json);
      }
    } catch (e) {
      setFireError(e?.message || "Could not reach the proxy server");
    } finally {
      setFiring(false);
    }
  };

  const handleCopyCurl = () => {
    const curl = buildCurl({ method, url, headersText, bodyText });
    doCopy(curl, "cURL");
  };

  const originalResponseText = bodyToText(request?.responseBody);
  const canDiff = !!(originalResponseText && result && result.body != null && String(result.body).length > 0);

  const handleDiff = () => {
    setDiffOpen(true);
    const leftParsed = tryPrettyJson(originalResponseText);
    const rightParsed = tryPrettyJson(typeof result.body === "string" ? result.body : bodyToText(result.body));
    if (!leftParsed.ok || !rightParsed.ok) {
      const which = !leftParsed.ok && !rightParsed.ok
        ? "Neither the original nor the replay response is valid JSON"
        : !leftParsed.ok
          ? "Original response isn't valid JSON"
          : "Replay response isn't valid JSON";
      setDiffNote(which);
      setDiffResult(null);
      return;
    }
    setDiffNote(null);
    try {
      setDiffResult(jsonDiff(leftParsed.value, rightParsed.value, {}));
    } catch (e) {
      setDiffResult({ same: false, counts: { added: 0, removed: 0, changed: 0, same: 0 }, entries: [], error: e.message });
    }
  };

  const prettyResponseBody = result
    ? (typeof result.body === "string" ? prettyPrintMaybe(result.body) : bodyToText(result.body))
    : "";

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={m.modal} onClick={(e) => e.stopPropagation()}>
        <div style={m.header}>
          <strong>Replay</strong>
          <button style={m.btn} onClick={onClose}>Close</button>
        </div>

        <div style={{ padding: 12, display: "grid", gap: 12 }}>
          <div style={s.fieldRow}>
            <div style={{ ...s.fieldCol, flex: "0 0 140px" }}>
              <div style={s.paneLabel}>Method</div>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                style={s.select}
              >
                {["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"].map((mth) => (
                  <option key={mth} value={mth}>{mth}</option>
                ))}
              </select>
            </div>
            <div style={{ ...s.fieldCol, flex: 1 }}>
              <div style={s.paneLabel}>URL</div>
              <textarea
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…"
                style={{ ...s.textarea, minHeight: 48, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                spellCheck={false}
              />
            </div>
          </div>

          <div style={s.fieldCol}>
            <div style={s.paneLabel}>Headers (one per line, key: value)</div>
            <textarea
              value={headersText}
              onChange={(e) => setHeadersText(e.target.value)}
              placeholder="content-type: application/json"
              style={{ ...s.textarea, minHeight: 100 }}
              spellCheck={false}
            />
          </div>

          <div style={s.fieldCol}>
            <div style={s.paneLabel}>Body</div>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder="(no body)"
              style={{ ...s.textarea, minHeight: 100 }}
              spellCheck={false}
            />
          </div>

          <div style={s.toolbar}>
            <button style={s.btnPrimary} onClick={handleFire} disabled={firing || !url}>
              {firing ? "Firing…" : "Fire"}
            </button>
            <button style={s.btn} onClick={handleCopyCurl}>Copy as cURL</button>
            <button style={s.btn} onClick={handleDiff} disabled={!canDiff} title={canDiff ? "" : "Needs both an original response and a replay response"}>
              Diff vs original response
            </button>
            {copyFeedback && <span style={s.copyFeedback}>{copyFeedback}</span>}
          </div>

          {fireError && <div style={s.errorHint}>{fireError}</div>}

          {result && (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ ...s.statusChip, ...statusStyle(result.status) }}>
                  {result.status ?? "—"}
                </span>
                <span style={s.copyFeedback}>{Number.isFinite(result.durationMs) ? `${result.durationMs} ms` : ""}</span>
              </div>

              <div>
                <button style={s.btn} onClick={() => setHeadersOpen((v) => !v)}>
                  {headersOpen ? "Hide" : "Show"} response headers
                </button>
                {headersOpen && (
                  <pre style={s.pre}>{headersObjToText(result.headers) || "(no headers)"}</pre>
                )}
              </div>

              <div>
                <div style={s.paneLabel}>Response body</div>
                <pre style={s.pre}>{prettyResponseBody || "(empty body)"}</pre>
                {result.bodyTruncated && <div style={s.truncNote}>Response body truncated by the proxy.</div>}
              </div>
            </div>
          )}

          {diffOpen && (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={s.paneLabel}>Diff vs original response</div>
              {diffNote && <div style={s.hint}>{diffNote}</div>}
              {!diffNote && <ReplayDiffResult result={diffResult} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── styles — mirrors DiffTool.jsx's CSS-var idiom ── */

const s = {
  fieldRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  fieldCol: { display: "grid", gap: 6, minWidth: 0 },
  paneLabel: { fontWeight: 600, fontSize: 12, color: "var(--ink)" },
  select: {
    padding: "8px 10px", fontSize: 12, border: "1px solid var(--line)", borderRadius: 8,
    background: "var(--bg)", color: "var(--ink)",
  },
  textarea: {
    minHeight: 60,
    resize: "vertical",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    lineHeight: 1.4,
    padding: 10,
    border: "1px solid var(--line)",
    borderRadius: 8,
    background: "var(--bg)",
    color: "var(--ink)",
    whiteSpace: "pre",
    overflowWrap: "normal",
  },
  toolbar: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  btn: {
    padding: "6px 10px", fontSize: 12, border: "1px solid var(--line)",
    borderRadius: 8, background: "var(--card)", cursor: "pointer", color: "var(--ink)",
  },
  btnPrimary: {
    padding: "6px 12px", fontSize: 12, border: "1px solid var(--primary, var(--line))",
    borderRadius: 8, background: "var(--primary, var(--card))", cursor: "pointer", color: "var(--primary-contrast, var(--ink))",
    fontWeight: 600,
  },
  copyFeedback: { fontSize: 12, color: "var(--muted)" },
  hint: { fontSize: 12, color: "var(--muted)", fontStyle: "italic" },
  errorHint: { fontSize: 12, color: "var(--error-text)" },
  summary: { fontWeight: 600, fontSize: 13, color: "var(--ink)" },
  truncNote: { fontWeight: 400, color: "var(--warn-text)", fontSize: 12 },
  statusChip: {
    display: "inline-block", padding: "3px 10px", fontSize: 12, fontWeight: 700,
    border: "1px solid var(--line)", borderRadius: 999,
  },
  pre: {
    margin: 0, padding: 10, fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.4,
    border: "1px solid var(--line)", borderRadius: 8, background: "var(--bg)", color: "var(--ink)",
    whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 320, overflow: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12, color: "var(--ink)" },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--line)", color: "var(--muted)" },
  k: {
    width: 260, padding: "6px 8px", borderBottom: "1px solid var(--line)",
    verticalAlign: "top", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere",
  },
  v: { padding: "6px 8px", borderBottom: "1px solid var(--line)", verticalAlign: "top", wordBreak: "break-word" },
};

const m = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 9999,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  },
  modal: {
    width: "min(1200px, 98vw)", maxHeight: "92vh", overflow: "auto",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,.25)", color: "var(--ink)",
  },
  header: {
    padding: "10px 12px", borderBottom: "1px solid var(--line)", background: "var(--bg)",
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
    color: "var(--ink)",
  },
  btn: {
    padding: "6px 10px", fontSize: 12, border: "1px solid var(--line)",
    borderRadius: 8, background: "var(--card)", cursor: "pointer", color: "var(--ink)",
  },
};

export default ReplayModal;
